import { supabaseClient } from '@/lib/_utils/supabase/client';
import { FunctionsResponse } from '@supabase/functions-js';
import { GetTickerHistoryRequestSchema, GetTickerHistoryResponseSchema } from '@tralent/edge-functions';
import { initialTicketHistoryDataValues, initialSelectedWatchListSymbol } from '@/lib/tournament/_utils';
import { StateCreator } from 'zustand';
import { ChartProps } from '@/components/charts/TVChartContainer';
import { TickerHistoryItem, WatchListDataProps } from '@/lib/tournament/_utils/interfaces';

export type TournamentArenaStore = {
	chartIsLoading: boolean;
	setChartIsLoading: (isLoading: boolean) => void;
	chartType: ChartProps['type'];
	setChartType: (type: ChartProps['type']) => void;
	tickerHistoryFreq: string;
	setTickerHistoryFreq: (freq: string) => void;
	selectedWatchListSymbol: WatchListDataProps;
	setSelectedWatchListSymbol: (symbol: WatchListDataProps) => void;
	tickerHistoryData: TickerHistoryItem[];
	setTickerHistoryData: (data: TickerHistoryItem[]) => void;
	getTickerHistory: (request: GetTickerHistoryRequestSchema) => Promise<GetTickerHistoryResponseSchema['data']>;
	resetStore: () => void;
};

const arenaStoreInitialValues: Omit<
	TournamentArenaStore,
	| 'setChartIsLoading'
	| 'setChartType'
	| 'setTickerHistoryFreq'
	| 'setSelectedWatchListSymbol'
	| 'setTickerHistoryData'
	| 'getTickerHistory'
	| 'resetStore'
> = {
	chartIsLoading: false,
	chartType: 'candlestick',
	tickerHistoryFreq: '1m',
	selectedWatchListSymbol: initialSelectedWatchListSymbol,
	tickerHistoryData: initialTicketHistoryDataValues,
};

export const createTournamentArenaSlice: StateCreator<TournamentArenaStore> = (set) => ({
	...arenaStoreInitialValues,
	setChartIsLoading: (isLoading) => set(() => ({ chartIsLoading: isLoading })),
	setChartType: (type) => set(() => ({ chartType: type })),
	setTickerHistoryFreq: (freq) => set(() => ({ tickerHistoryFreq: freq })),
	setSelectedWatchListSymbol: (symbol) => set(() => ({ selectedWatchListSymbol: symbol })),
	setTickerHistoryData: (data) => set(() => ({ tickerHistoryData: data })),
	getTickerHistory: async (request: GetTickerHistoryRequestSchema) => {
		set({ chartIsLoading: true });

		const functionParams = new URLSearchParams({
			...request,
			countBack: request.countBack?.toString() ?? '',
		});

		const { data: response, error }: FunctionsResponse<GetTickerHistoryResponseSchema> =
			await supabaseClient.functions.invoke('get-ticker-history?' + functionParams);

		if (error) {
			console.error('Error fetching ticker history:', error);
			set({
				tickerHistoryData: [{ name: '', data: [] }],
				chartIsLoading: false,
			});
		}

		set({
			tickerHistoryData: [{ name: '', data: response?.data }],
			chartIsLoading: false,
		});

		return response?.data ?? [];
	},
	resetStore: () => set(() => arenaStoreInitialValues),
});
